import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table } from 'reactstrap';
import PropertyCard from '../components/PropertyCard';
import { useAuth } from '../contexts/AuthContext';
import ConditionalNavBar from '../components/ConditionalNavBar';
import axiosInstance from '../api/Axios';

const PropertySearchResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [allProperties, setAllProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [searchParams, setSearchParams] = useState({});
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 10000 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [debugModal, setDebugModal] = useState(false);
  const [selectedPropertyForDebug, setSelectedPropertyForDebug] = useState(null);

  useEffect(() => {
    // Get search parameters from location state
    const params = location.state?.searchParams;
    
    if (!params) {
      // If no search params, redirect back to home
      navigate('/');
      return;
    }

    setSearchParams(params);
    fetchProperties(params);
  }, [location.state, navigate]);

  const fetchProperties = async (params) => {
    setLoading(true);
    setError('');
    
    try {
      // Build query string
      const queryParams = new URLSearchParams({
        campus: params.campus,
        check_in: params.checkIn,
        check_out: params.checkOut
      });
      
      if (params.gender) queryParams.append('gender', params.gender.toLowerCase());
      if (params.roomType) queryParams.append('room_type', params.roomType);
      if (params.bathroomType) queryParams.append('bathroom_type', params.bathroomType);
      
      const response = await axiosInstance.get(`/api/properties/search/?${queryParams.toString()}`);
      
      // Transform API response to match PropertyCard expectations
      const transformedProperties = response.data.results.map(prop => ({
        id: prop.id,
        title: prop.title,
        address: {
          street: prop.address.street,
          suburb: prop.address.suburb || 'N/A',
          city: prop.address.city,
          province: prop.address.province,
          postal_code: prop.address.postal_code
        },
        description: `${prop.total_rooms_with_availability} rooms with ${prop.total_available_beds} beds available`,
        category: prop.price_range.min < 3000 ? 'budget' : 'mid_range',
        distance_from_campus: 0, // TODO: Calculate from coordinates
        price_per_month: prop.price_range.min,
        price_range: prop.price_range,
        amenities: prop.amenities,
        accommodation_details: {
          room_types: prop.rooms.length > 0 ? [...new Set(prop.rooms.map(r => 
            r.no_of_beds === 1 ? 'single' : 'shared'
          ))] : ['single'],
          bathroom_type: prop.amenities.some(a => a.toLowerCase().includes('private') && a.toLowerCase().includes('bathroom')) 
            ? 'private' 
            : 'shared',
          gender_preference: params.gender ? `${params.gender}_only` : 'mixed',
          available_rooms: prop.total_rooms_with_availability,
          total_rooms: prop.total_rooms_with_availability
        },
        availability: {
          move_in_date: params.checkIn
        },
        total_available_beds: prop.total_available_beds,
        total_rooms_with_availability: prop.total_rooms_with_availability,
        rooms: prop.rooms.map(room => ({
          ...room,
          block_number: room.block_number || 'N/A',
          floor_number: room.floor_number || 'N/A'
        })),
        matching_room_ids: prop.rooms.map(r => r.id), // Store matching room IDs
        campus_id: parseInt(params.campus)
      }));
      
      setAllProperties(transformedProperties);
      setFilteredProperties(transformedProperties);
      
      // Set price range dynamically based on actual results
      if (transformedProperties.length > 0) {
        const allPrices = transformedProperties.flatMap(prop => 
          prop.rooms.map(room => parseFloat(room.room_type?.semester_price || 0))
        ).filter(price => price > 0);
        
        if (allPrices.length > 0) {
          const minPrice = Math.min(...allPrices);
          const maxPrice = Math.max(...allPrices);
          setPriceRange({ min: 0, max: maxPrice });
          console.log(`Dynamic price range set: R0 - R${maxPrice}`);
        }
      }
      
      // Console log all room IDs from all properties
      console.log('=== PROPERTY SEARCH RESULTS - ALL ROOM IDs ===');
      transformedProperties.forEach((prop, idx) => {
        console.log(`Property ${idx + 1}: ${prop.title} (ID: ${prop.id})`);
        console.log(`  Total Rooms: ${prop.rooms?.length || 0}`);
        console.log(`  Room IDs:`, prop.matching_room_ids);
        if (prop.rooms && prop.rooms.length > 0) {
          prop.rooms.forEach(room => {
            console.log(`    - Room ${room.room_number} (ID: ${room.id}): ${room.available_beds_count} beds available, Price: R${room.room_type?.semester_price || 'N/A'}`);
          });
        }
      });
      console.log('===========================================');
    } catch (err) {
      console.error('Property search error:', err);
      setError(err.response?.data?.error || 'Failed to load properties. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Real-time filtering when amenities or price range changes
  useEffect(() => {
    if (allProperties.length > 0) {
      let filtered = [...allProperties];

      // Filter by selected amenities
      if (selectedAmenities.length > 0) {
        filtered = filtered.filter(property =>
          selectedAmenities.every(amenity =>
            property.amenities.some(a => a.toLowerCase() === amenity.toLowerCase())
          )
        );
      }

      // Filter by price range
      filtered = filtered.filter(property =>
        property.price_per_month >= priceRange.min &&
        property.price_per_month <= priceRange.max
      );

      setFilteredProperties(filtered);
      
      // Console log filtered room IDs
      console.log('=== FILTERED RESULTS - ROOM IDs ===');
      console.log(`Applied Filters:`);
      console.log(`  - Price Range: R${priceRange.min} - R${priceRange.max}`);
      console.log(`  - Selected Amenities:`, selectedAmenities);
      console.log(`Filtered Properties: ${filtered.length} of ${allProperties.length}`);
      filtered.forEach((prop, idx) => {
        console.log(`Property ${idx + 1}: ${prop.title} (ID: ${prop.id})`);
        console.log(`  Room IDs:`, prop.matching_room_ids);
      });
      console.log('===================================');
    }
  }, [selectedAmenities, priceRange, allProperties]);

  const getCampusName = () => {
    // Since we're querying by campus, we can just use a simple display
    return 'Selected Campus';
  };

  // Get all unique amenities from properties
  const getAllAmenities = () => {
    const amenitiesSet = new Set();
    allProperties.forEach(property => {
      property.amenities.forEach(amenity => {
        amenitiesSet.add(amenity);
      });
    });
    return Array.from(amenitiesSet).sort();
  };

  // Handle amenity filter toggle
  const handleAmenityToggle = (amenity) => {
    setSelectedAmenities(prev => {
      if (prev.includes(amenity)) {
        return prev.filter(a => a !== amenity);
      } else {
        return [...prev, amenity];
      }
    });
  };

  // Handle price range change
  const handlePriceRangeChange = (min, max) => {
    setPriceRange({ min, max });
  };

  // Get amenity display name
  const getAmenityDisplayName = (amenity) => {
    // Convert snake_case or spaces to Title Case
    return amenity
      .split(/[_\s]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (loading) {
    return (
      <div>
        <ConditionalNavBar />
        <div className="container mt-5">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Searching for properties...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <ConditionalNavBar />
        <div className="container mt-5">
          <div className="alert alert-danger" role="alert">
            <h5>Search Error</h5>
            <p>{error}</p>
            <button 
              className="btn btn-primary mt-2"
              onClick={() => navigate('/')}
            >
              Back to Search
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Handler functions for property actions
  const handleViewDetails = (property) => {
    // For now, just log the property details
    console.log('View details for property:', property);
  };

  const handleDebugProperty = (property) => {
    setSelectedPropertyForDebug(property);
    setDebugModal(true);
  };

  const handleBookProperty = (property) => {
    // Filter rooms based on current price range
    // Only include rooms whose price falls within the selected range
    let matchingRooms = property.rooms || [];
    
    if (property.rooms && property.rooms.length > 0) {
      matchingRooms = property.rooms.filter(room => {
        const roomPrice = room.room_type?.semester_price || property.price_per_month;
        return roomPrice >= priceRange.min && roomPrice <= priceRange.max;
      });
    }
    
    // If amenities are selected, ensure the property still matches
    // (This is already handled by filteredProperties, but being extra safe)
    const matchingRoomIds = matchingRooms.map(r => r.id);
    
    console.log('Booking property with filtered rooms:', {
      propertyId: property.id,
      totalRooms: property.rooms?.length || 0,
      matchingRooms: matchingRoomIds.length,
      priceRange: priceRange,
      selectedAmenities: selectedAmenities
    });
    
    // Navigate to booking page with property and filtered room IDs
    navigate('/property-booking', {
      state: {
        property: property,
        searchParams: searchParams,
        bookingDates: {
          checkIn: searchParams.checkIn,
          checkOut: searchParams.checkOut
        },
        matchingRoomIds: matchingRoomIds // Pass filtered room IDs based on refinement
      }
    });
  };

  return (
    <div>
      <ConditionalNavBar />
      {/* Top login prompt shown when user is not authenticated */}
      {!isAuthenticated && (
        <div className="container mt-3">
          <div className="alert alert-info mb-0" role="alert">
            Please login to request a booking
          </div>
        </div>
      )}
      
      {/* Search Summary Header */}
      <div className="bg-light py-4">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2 className="mb-2">Student Accommodation</h2>
              <p className="text-muted mb-0">
                {searchParams.campus && `Near ${getCampusName()}`}
                {searchParams.checkIn && ` • ${searchParams.checkIn} - ${searchParams.checkOut}`}
                {searchParams.gender && ` • ${searchParams.gender}`}
                {searchParams.roomType && ` • ${searchParams.roomType} Room`}
                {searchParams.bathroomType && ` • ${searchParams.bathroomType} Bathroom`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div className="container my-5">
        <div className="row mb-4">
          <div className="col-12">
            <h4>{filteredProperties.length} properties found</h4>
            <p className="text-muted">Student accommodation matching your criteria</p>
          </div>
        </div>

        <div className="row">
          {/* Sidebar Filters */}
          <div className="col-lg-3 col-md-4 mb-4">
            <div className="card sticky-top" style={{ top: '20px' }}>
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="fas fa-filter me-2"></i>
                  Refine Results
                </h5>
              </div>
              <div className="card-body">
                {/* Price Range Filter */}
                <div className="mb-4">
                  <h6 className="fw-bold mb-3">Price Range</h6>
                  <div className="row g-2">
                    <div className="col-6">
                      <label className="form-label small">Min</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={priceRange.min}
                        onChange={(e) => handlePriceRangeChange(parseInt(e.target.value) || 0, priceRange.max)}
                        placeholder="Min"
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small">Max</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={priceRange.max}
                        onChange={(e) => handlePriceRangeChange(priceRange.min, parseInt(e.target.value) || 10000)}
                        placeholder="Max"
                      />
                    </div>
                  </div>
                  <small className="text-muted">ZAR per month</small>
                </div>

                {/* Amenities Filter */}
                <div className="mb-4">
                  <h6 className="fw-bold mb-3">Amenities & Features</h6>
                  <div className="amenities-filter" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {getAllAmenities().map((amenity) => (
                      <div key={amenity} className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`amenity-${amenity}`}
                          checked={selectedAmenities.includes(amenity)}
                          onChange={() => handleAmenityToggle(amenity)}
                        />
                        <label className="form-check-label small" htmlFor={`amenity-${amenity}`}>
                          {getAmenityDisplayName(amenity)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Clear Filters */}
                <div className="d-grid">
                  <button 
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setSelectedAmenities([]);
                      setPriceRange({ min: 0, max: 10000 });
                    }}
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Properties Grid */}
          <div className="col-lg-9 col-md-8">
            {filteredProperties.length > 0 ? (
              <div className="row">
                {filteredProperties.map(property => (
                  <div key={property.id} className="col-lg-4 col-xl-4 col-md-6 mb-4">
                    <PropertyCard 
                      property={property} 
                      onViewDetails={handleViewDetails}
                      onBook={handleBookProperty}
                      onDebug={handleDebugProperty}
                      isAuthenticated={isAuthenticated}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-5">
                <div className="mb-4">
                  <i className="fas fa-search fa-3x text-muted"></i>
                </div>
                <h5>No properties found</h5>
                <p className="text-muted">
                  Try adjusting your filters or 
                  <button 
                    className="btn btn-link p-0 ms-1"
                    onClick={() => {
                      setSelectedAmenities([]);
                      setPriceRange({ min: 0, max: 10000 });
                    }}
                  >
                    clear all filters
                  </button>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Back to Search Button */}
      <div className="container mb-5">
        <div className="text-center">
          <button 
            className="btn btn-outline-primary"
            onClick={() => navigate('/')}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Search
          </button>
        </div>
      </div>

      <style jsx="true">{`
        .amenities-filter {
          border: 1px solid #e9ecef;
          border-radius: 0.375rem;
          padding: 0.75rem 1rem 0.75rem 1.25rem;
          background-color: #f8f9fa;
        }
        
        .form-check {
          padding: 0.25rem 0.5rem;
          margin-bottom: 0.5rem !important;
          border-radius: 0.25rem;
          transition: background-color 0.15s ease-in-out;
          min-height: 2rem;
          display: flex;
          align-items: center;
        }
        
        .form-check:hover {
          background-color: rgba(0, 123, 255, 0.08);
        }
        
        .form-check-label {
          word-wrap: break-word;
          line-height: 1.3;
          margin-left: 0.25rem;
          flex: 1;
          cursor: pointer;
        }
        
        .form-check-input {
          margin-top: 0;
          flex-shrink: 0;
          cursor: pointer;
        }
        
        .form-check-input:checked {
          background-color: #007bff;
          border-color: #007bff;
        }
        
        .sticky-top {
          position: sticky !important;
        }
        
        @media (max-width: 768px) {
          .sticky-top {
            position: relative !important;
          }
          
          .form-check-label {
            font-size: 0.875rem;
          }
        }
      `}</style>

      {/* Debug Modal */}
      <Modal isOpen={debugModal} toggle={() => setDebugModal(false)} size="lg">
        <ModalHeader toggle={() => setDebugModal(false)}>
          Debug Room Information - {selectedPropertyForDebug?.title}
        </ModalHeader>
        <ModalBody>
          {selectedPropertyForDebug && (
            <>
              <div className="mb-3">
                <strong>Property ID:</strong> {selectedPropertyForDebug.id}<br/>
                <strong>Total Rooms:</strong> {selectedPropertyForDebug.rooms?.length || 0}<br/>
                <strong>Matching Room IDs:</strong> [{selectedPropertyForDebug.matching_room_ids?.join(', ')}]
              </div>
              
              <Table striped bordered size="sm">
                <thead>
                  <tr>
                    <th>Room ID</th>
                    <th>Room Number</th>
                    <th>Block</th>
                    <th>Floor</th>
                    <th>Gender</th>
                    <th>Room Type</th>
                    <th>Beds</th>
                    <th>Available</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedPropertyForDebug.rooms?.map(room => (
                    <tr key={room.id}>
                      <td>{room.id}</td>
                      <td>{room.room_number}</td>
                      <td>{room.block_number || 'N/A'}</td>
                      <td>{room.floor_number || 'N/A'}</td>
                      <td>
                        <span className="badge bg-secondary">
                          {room.room_gender || 'N/A'}
                        </span>
                      </td>
                      <td>{room.room_type?.name || 'N/A'}</td>
                      <td>{room.no_of_beds}</td>
                      <td>
                        <span className="badge bg-success">
                          {room.available_beds_count}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDebugModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PropertySearchResults;